import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { SocialImageView, useSocialMediaRepositoryToSetImages } from '@bingads-webui-campaign-react/add-social-images-view';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { ROOT_FOLDER_ID } from '@bingads-webui-campaign-react/asset-library-model';

import { validateImageSizeAsync } from '@bingads-webui-campaign/image-experience-utils';

import { VIEWS } from '../view-type';

const ManagedSocialImageView = ({
  socialImageConfig,
  setShowBlocker,
  imageExperienceProps,
  assetLibraryConfig,
  assetLibraryDataService,
  logger,
}) => {
  const { socialMediaDataSource } = socialImageConfig;
  const { onImageSelected, onImageAddError } = assetLibraryConfig;

  const {
    finalUrl,
    onUpdateFinalUrl,
    finalUrlProtocol,
    onUpdateFinalUrlPrefix,
    imageOption,
    isMultimediaAd,
  } = imageExperienceProps;

  const { images, socialMediaAccount } =
    useSocialMediaRepositoryToSetImages({ socialMediaDataSource });

  const fetchImages =
    useCallback(url => socialMediaDataSource.fetchImages(url), [socialMediaDataSource]);

  const fetchImageByAccount =
    useCallback(acct => socialMediaDataSource.fetchImageByAccount(acct), [socialMediaDataSource]);

  const onSelectImage = useCallback((url) => {
    setShowBlocker(true);

    const addImagePromise = validateImageSizeAsync(url, null, isMultimediaAd, imageOption)
      .then(() => assetLibraryDataService.uploadImageByUrl({
        imageUrl: url,
        folderId: ROOT_FOLDER_ID,
        sourceType: constants.sourceTypes.TwitterImported,
      }))
      .then(id => onImageSelected({ id }, constants.imageSources.Social))
      .catch(e => onImageAddError(e));

    addImagePromise
      .tap((img) => {
        if (img && img.errorMessage) {
          logger.logTrace(`Ad creation flow - add social image error message: ${img.errorMessage}`);
        } else {
          const { id: imageId } = img || {};
          logger.logTrace('Ad creation flow - selected social image', { imageId });
        }
      })
      .finally(() => {
        setShowBlocker(false);
      });
  }, [setShowBlocker, isMultimediaAd, imageOption, assetLibraryDataService, onImageSelected, onImageAddError, logger]);

  return (
    <SocialImageView
      images={images}
      socialMediaAccount={socialMediaAccount}
      addImageByUrl={onSelectImage}
      fetchImages={fetchImages}
      fetchImageByAccount={fetchImageByAccount}
      finalUrl={finalUrl}
      onUpdateFinalUrl={onUpdateFinalUrl}
      finalUrlProtocol={finalUrlProtocol}
      onUpdateFinalUrlPrefix={onUpdateFinalUrlPrefix}
      logger={logger}
    />
  );
};

ManagedSocialImageView.propTypes = {
  socialImageConfig: PropTypes.shape({
    socialMediaDataSource: PropTypes.shape({
      fetchImageByAccount: PropTypes.func,
      fetchImages: PropTypes.func,
      subscribe: PropTypes.func,
      data: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        src: PropTypes.string,
      })),
      socialMediaAccounts: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  imageExperienceProps: PropTypes.shape({
    finalUrl: PropTypes.string,
    onUpdateFinalUrl: PropTypes.func,
    finalUrlProtocol: PropTypes.string,
    onUpdateFinalUrlPrefix: PropTypes.func,
  }).isRequired,
  setShowBlocker: PropTypes.func.isRequired,
  assetLibraryDataService: PropTypes.shape({}).isRequired,
  assetLibraryConfig: PropTypes.shape({
    onImageSelected: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
};

ManagedSocialImageView.defaultProps = {
};

export const socialImageSourceConfig = {
  title: _TL_('Social'),
  showBackButton: true,
  onRenderContent: props => (
    <ManagedSocialImageView
      {...props}
    />
  ),
  buttonConfig: {
    label: _TL_('Social'),
    view: VIEWS.SocialMedia,
    iconName: 'People',
    buttonLocator: 'social-media',
  },
};
